import React, { useState } from 'react';
import { Alert, Button, Card, Col, Empty, Progress, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import {
  CloseOutlined,
  ExportOutlined,
  LinkOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { OrderTransportsDto, RouteOrderDto, TransportRouteDetailDto } from '../../../dto/transport';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatCargoQuantitySummary } from '../../../utils/cargoQuantity';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { normalizeTransportAddressList } from '../transportAddressUtils';
import OrderTransportLinkModal from './OrderTransportLinkModal';

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'default',
  SCHEDULED: 'blue',
  LOADING: 'cyan',
  IN_TRANSIT: 'orange',
  DELIVERED: 'green',
  CANCELLED: 'red',
};

interface OrderTransportsSectionProps {
  orderId: string;
  orderTransports: OrderTransportsDto;
  loading: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onDelete: (transportId: string) => Promise<void> | void;
}

/**
 * Transport tab on the order detail page.
 *
 * A transport is an independent N:N entity that an order is LINKED to (not
 * owned by the order) — the UI copy uses "Powiąż transport" / "Link transport"
 * to match. The per-transport card here shows only the per-order slice
 * (carrier, dates, THIS order's cargo allocation) and deep-links to the
 * Transports tab for full edits. Curating the full form from inside the order
 * was the original UX pain.
 */
const OrderTransportsSection: React.FC<OrderTransportsSectionProps> = ({
  orderId,
  orderTransports,
  loading,
  canEdit,
  canDelete,
  onDelete,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const navigate = useNavigate();
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  if (loading) {
    return (
      <Card>
        <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
      </Card>
    );
  }

  const handleOpenTransport = (transportId: string) =>
    navigate(`/app/transports/${transportId}`);

  const linkButton = canEdit ? (
    <Button type="primary" icon={<LinkOutlined />} onClick={() => setLinkModalOpen(true)}>
      {t('transport:orderTransports.linkTransport')}
    </Button>
  ) : null;

  const linkModal = (
    <OrderTransportLinkModal
      open={linkModalOpen}
      orderId={orderId}
      linkedTransportIds={orderTransports.transports.map((transport) => transport.id)}
      onClose={() => setLinkModalOpen(false)}
    />
  );

  if (orderTransports.transports.length === 0) {
    return (
      <>
        <Card>
          <Empty
            image={<TruckOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
            description={t('transport:orderTransports.empty')}
          >
            {linkButton}
          </Empty>
        </Card>
        {linkModal}
      </>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert type="info" showIcon message={t('transport:splitHint')} />

      {orderTransports.productRealization.length > 0 && (
        <Card
          title={t('transport:orderTransports.realizationTitle')}
          extra={linkButton}
        >
          <Table
            rowKey="orderProductId"
            dataSource={orderTransports.productRealization}
            pagination={false}
            size="small"
            columns={[
              {
                title: t('transport:orderTransports.productColumn'),
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: t('transport:orderTransports.orderedColumn'),
                key: 'ordered',
                width: 150,
                render: (_, row) => `${row.orderedQuantity} ${formatUnit(row.unit)}`,
              },
              {
                title: t('transport:orderTransports.allocatedColumn'),
                key: 'allocated',
                width: 150,
                render: (_, row) => `${row.allocatedQuantity} ${formatUnit(row.unit)}`,
              },
              {
                title: t('transport:orderTransports.remainingColumn'),
                key: 'remaining',
                width: 150,
                render: (_, row) => `${row.remainingQuantity} ${formatUnit(row.unit)}`,
              },
              {
                title: t('transport:orderTransports.progressColumn'),
                key: 'percent',
                width: 220,
                render: (_, row) => {
                  const percent = Math.min(row.percent, 100);
                  const over = row.percent > 100;
                  return (
                    <Progress
                      percent={percent}
                      size="small"
                      status={over ? 'exception' : row.percent >= 100 ? 'success' : 'active'}
                      format={() => `${row.percent}%`}
                    />
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      {orderTransports.transports.map((transport) => (
        <OrderTransportCard
          key={transport.id}
          orderId={orderId}
          transport={transport}
          canDelete={canDelete}
          onDelete={onDelete}
          onOpen={() => handleOpenTransport(transport.id)}
        />
      ))}
      {linkModal}
    </Space>
  );
};

interface OrderTransportCardProps {
  orderId: string;
  transport: TransportRouteDetailDto;
  canDelete: boolean;
  onDelete: (transportId: string) => Promise<void> | void;
  onOpen: () => void;
}

/**
 * Compact summary of a transport seen from the order's point of view.
 * Shows only:
 *   - carrier + status + route number (header)
 *   - loading + estimated delivery dates
 *   - first loading / delivery address (for orientation)
 *   - the THIS-order slice of the shared cargo (qty, LDM, allocated cost)
 *   - a "shared with N other orders" hint when the transport carries more
 *     than just this order
 * Full details (every address, driver, vehicle, notes, all linked orders)
 * live on the Transports tab — "Otwórz transport" deep-links there.
 */
const OrderTransportCard: React.FC<OrderTransportCardProps> = ({
  orderId,
  transport,
  canDelete,
  onDelete,
  onOpen,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const routeOrder = transport.routeOrders.find((ro: RouteOrderDto) => ro.orderId === orderId);
  const otherOrdersCount = Math.max(transport.routeOrders.length - 1, 0);

  const firstLoading = normalizeTransportAddressList(transport.loadingAddresses)[0];
  const firstDelivery = normalizeTransportAddressList(transport.deliveryAddresses)[0];
  const cargoItemsForOrder = transport.cargoItems.filter((item) => item.orderId === orderId);
  const cargoQuantitySummary = routeOrder
    ? formatCargoQuantitySummary(
        cargoItemsForOrder.length > 0 ? cargoItemsForOrder : routeOrder.order?.products || [],
        i18n.language,
      )
    : null;
  const slice = routeOrder
    ? [
        cargoQuantitySummary === '-' ? null : cargoQuantitySummary,
        routeOrder.ldm ? `${routeOrder.ldm.toLocaleString(
          i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} LDM` : null,
        routeOrder.volume ? `${formatLocalizedNumber(routeOrder.volume, 3, i18n.language)} m³` : null,
        routeOrder.weight ? `${formatLocalizedNumber(routeOrder.weight, 2, i18n.language)} kg` : null,
      ].filter(Boolean)
    : [];

  return (
    <Card
      size="small"
      title={
        <Space>
          <TruckOutlined />
          <span>
            {transport.routeNumber ? `${transport.routeNumber} · ` : ''}
            {transport.transportCompany || '-'}
          </span>
          <Tag color={STATUS_COLORS[transport.status] || 'default'}>
            {t(`transport:status.${transport.status.toLowerCase()}`)}
          </Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ExportOutlined />} onClick={onOpen}>
            {t('transport:orderTransports.openTransport')}
          </Button>
          {canDelete && (
            <Button danger icon={<CloseOutlined />} onClick={() => onDelete(transport.id)}>
              {t('transport:orderTransports.unlink')}
            </Button>
          )}
        </Space>
      }
    >
      <Row gutter={[16, 8]}>
        <Col xs={24} md={12}>
          <Text type="secondary">{t('transport:fields.loadingDate')}: </Text>
          <Text>{formatDate(transport.loadingDate) || '-'}</Text>
          {firstLoading && (
            <div>
              <Text type="secondary">{firstLoading}</Text>
            </div>
          )}
        </Col>
        <Col xs={24} md={12}>
          <Text type="secondary">{t('transport:fields.estimatedDelivery')}: </Text>
          <Text>{formatDate(transport.estimatedDelivery) || '-'}</Text>
          {firstDelivery && (
            <div>
              <Text type="secondary">{firstDelivery}</Text>
            </div>
          )}
        </Col>
        {slice.length > 0 && (
          <Col xs={24}>
            <Text strong>{t('transport:orderTransports.thisOrderShare')}: </Text>
            <Text>{slice.join(' · ')}</Text>
            {routeOrder?.allocatedTransportCost != null && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({formatCurrency(Number(routeOrder.allocatedTransportCost), transport.currency)})
              </Text>
            )}
          </Col>
        )}
        {otherOrdersCount > 0 && (
          <Col xs={24}>
            <Tag color="processing">
              {t('transport:orderTransports.sharedWithSummary', { count: otherOrdersCount })}
            </Tag>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default OrderTransportsSection;
